<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, ref } from "vue"
import { makeDroppable } from "@vue-dnd-kit/core"
import type { IDragEvent } from "@vue-dnd-kit/core"
import { useRedirect } from "@core/composables"
import { useResponsive } from "@core/composables/useResponsive"
import { useHomeWorkspaceStore } from "@modules/home/stores/homeStore.store"
import {
    applyDropToFolder,
    DASHBOARD_WORKSPACE_DND_GROUP,
    type WorkspaceDragPayload,
} from "@modules/home/composables/homeDnDHandler"

defineOptions({
    name: "HomeWorkspaceBreadcrumbItem",
})

// ================================
// TYPES & INTERFACES
// ================================
export type WorkspaceBreadcrumbModelItem = {
    label: string
    icon?: string
    route?: string
    /** Dossier cible pour le dépôt (racine = `null`). */
    dropTargetFolderId: string | null
}

// ================================
// PROPS
// ================================
const props = defineProps<{
    item: WorkspaceBreadcrumbModelItem
    /** Attributs PrimeVue pour l’item de fil d’Ariane. */
    propsItem: { action?: Record<string, unknown> }
    /** Désactive les zones de dépôt (ex. utilisateur en lecture seule). */
    disabledDrop?: boolean
}>()

// ================================
// COMPOSABLES
// ================================
const { redirectWrapper } = useRedirect()
const { isTablet } = useResponsive()
const homeWorkspace = useHomeWorkspaceStore()

// ================================
// STATE
// ================================
const dropRef = ref<HTMLElement | null>(null)
const suppressNavClick = ref(false)

const SUPPRESS_NAV_CLICK_FALLBACK_MS = 250

const getDropDisabled = computed(() => !!props.disabledDrop || isTablet.value)

// ================================
// DND — ZONE DE DÉPÔT
// ================================
const { isAllowed: getIsDropAllowed, isDragOver: getIsDragOver } =
    makeDroppable(dropRef, {
        groups: [DASHBOARD_WORKSPACE_DND_GROUP],
        disabled: getDropDisabled,
        placementMargins: { top: 0, right: 0, bottom: 0, left: 0 },
        data: () => ({ folderId: props.item.dropTargetFolderId }),
        events: {
            onDrop(e: IDragEvent) {
                const src = e.draggedItems[0]?.data as
                    | WorkspaceDragPayload
                    | undefined
                if (!src) return
                applyDropToFolder(
                    homeWorkspace,
                    props.item.dropTargetFolderId,
                    src
                )
                armSuppressNavClickAfterDrop()
            },
        },
    })

const getCrumbDropOverClass = computed(() => {
    if (getDropDisabled.value) return ""
    if (!getIsDragOver.value || !getIsDropAllowed.value) return ""
    return "dashboard-workspace-breadcrumb-item--drag-over"
})

// ================================
// INTERACTIONS
// ================================
function armSuppressNavClickAfterDrop(): void {
    suppressNavClick.value = true
    window.setTimeout(() => {
        suppressNavClick.value = false
    }, SUPPRESS_NAV_CLICK_FALLBACK_MS)
}

/**
 * Évite la navigation si le clic suit un dépôt sur la même zone.
 */
function onCrumbClickCapture(ev: MouseEvent): void {
    if (!suppressNavClick.value) return
    ev.preventDefault()
    ev.stopPropagation()
    suppressNavClick.value = false
}

function onNavigate(route: string): void {
    if (suppressNavClick.value) {
        suppressNavClick.value = false
        return
    }
    redirectWrapper(route)
}
</script>

<template>
    <!-- ================================ -->
    <!-- SEGMENT FIL D’ARIANE (DND) -->
    <!-- ================================ -->
    <span
        ref="dropRef"
        class="dashboard-workspace-breadcrumb-item inline-flex max-w-full min-w-0 items-center rounded px-0.5 ml-1"
        :class="getCrumbDropOverClass"
        @click.capture="onCrumbClickCapture"
    >
        <div class="flex min-w-0 max-w-full items-center gap-2">
            <i
                v-if="item.icon"
                class="p-breadcrumb-item-icon shrink-0"
                :class="item.icon"
            />
            <button
                v-if="item.route"
                type="button"
                v-bind="propsItem.action"
                class="min-w-0 cursor-pointer hover:text-primary"
                @click.prevent="onNavigate(item.route)"
            >
                <span class="hover:underline truncate">{{ item.label }}</span>
            </button>
            <p v-else v-bind="propsItem.action" class="min-w-0">
                <span class="truncate">{{ item.label }}</span>
            </p>
        </div>
    </span>
</template>

<style scoped>
/* Halo vert au survol (aligné sur HomeFolderRow). */
.dashboard-workspace-breadcrumb-item--drag-over {
    box-shadow:
        0 0 0 2px rgb(34 197 94 / 0.45),
        0 4px 14px rgb(34 197 94 / 0.15);
}
</style>
